import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import RelatedDocumentItem from "./RelatedDocumentItem";
import DocumentPreviewDialog from "./DocumentPreviewDialog";
import useRelatedDocuments from "../hooks/useRelatedDocuments";
import { UploadInformationForm } from "../upload/UploadInformation";
import { useForm } from "react-hook-form";
import Button from "../shared/Button";
import dayjs from "dayjs";
import DocumentsService from "../../services/DocumentsService";

const RelatedDocuments = ({
  title = "Liste des documents liés",
  estateId,
  documentTypes,
  withNoInformation,
  isCreated,
}) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState();
  const [isEditMode, setIsEditMode] = useState(false);

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };
  const previewDocument = (doc) => {
    setSelectedDoc(doc);
    setOpenPreview(true);
  };

  const { data: relatedDocuments, refetch } = useRelatedDocuments({
    estateId,
    documentTypes,
    withNoInformation,
    isCreated,
  });

  const handleOpenEdit = (selectedDoc) => {
    setSelectedDoc(selectedDoc);
    setIsEditMode(true);
  };

  const handleCloseEditMode = () => {
    setSelectedDoc(null);
    setIsEditMode(false);
  };

  return (
    <StyleWrapper className="related-doc">
      {!isEditMode && (
        <>
          <h5 className="related-doc-title">{title}</h5>
          <div className="related-doc-list d-flex flex-column">
            {relatedDocuments.map((doc) => (
              <RelatedDocumentItem
                key={doc.id}
                document={doc}
                preview={previewDocument}
                refetch={refetch}
                handleOpenEdit={handleOpenEdit}
              />
            ))}
          </div>
        </>
      )}
      {isEditMode && (
        <EditRelatedDocument
          selectedDoc={selectedDoc}
          refetch={refetch}
          handleCloseEditMode={handleCloseEditMode}
        />
      )}
      <DocumentPreviewDialog
        open={openPreview}
        handleClose={handleClosePreview}
        documentInfo={selectedDoc}
      />
    </StyleWrapper>
  );
};

const EditRelatedDocument = ({ selectedDoc, handleCloseEditMode, refetch }) => {
  const { control, setValue, reset, handleSubmit } = useForm({
    defaultValues: {
      name: null,
      documentType: null,
      effectiveDate: null,
    },
  });

  useEffect(() => {
    if (!!selectedDoc) {
      setValue("name", selectedDoc.filename);
      setValue("documentType", selectedDoc.documentTypeId);
      setValue("effectiveDate", dayjs(selectedDoc.effectiveDate));
    } else if (!selectedDoc) {
      reset();
    }
  }, [selectedDoc]);

  const onUpdate = async (data) => {
    try {
      const { name, documentType, effectiveDate } = data;
      await DocumentsService.updateDoc(selectedDoc.id, {
        filename: name,
        documentTypeId: documentType,
        effectiveDate: effectiveDate.format("YYYY-MM-DD"),
      });
      !!refetch && refetch();
      handleCloseEditMode();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Stack gap={2} component="form" onSubmit={handleSubmit(onUpdate)}>
      <Stack direction="row" gap={1} alignItems="center">
        <IconButton onClick={handleCloseEditMode}>
          <ArrowBackIcon />
        </IconButton>
        <h5 className="related-doc-title">Modifier le document</h5>
      </Stack>
      <UploadInformationForm control={control} isRow={false} />
      <Stack direction="row" gap={2}>
        <Button onClick={handleCloseEditMode}>Annuler</Button>
        <Button color="success" variant="contained" type="submit">
          Sauvegarder
        </Button>
      </Stack>
    </Stack>
  );
};

const StyleWrapper = styled.div`
  &.related-doc {
    padding-bottom: 70px;
    height: 450px;
  }

  .related-doc-title {
    font-size: 26px;
    font-weight: 700;
    color: var(--text-color);
    margin-bottom: 0;
  }

  .related-doc-list {
    margin-top: 20px;
    padding-right: 20px;
    gap: 10px;
    max-height: 100%;
    overflow-y: auto;
  }
`;

export default RelatedDocuments;
